import { useQuery } from '@apollo/client';
import { ThemeProvider as HarfangThemeProvider, Layout } from '@elipssolution/harfang';
import { ThemeProvider } from '@mui/material';
import { ReactNode, useCallback, useMemo } from 'react';

import DesktopLayoutContent from './layoutContent/DesktopLayoutContent';
import MobileLayoutContent from './layoutContent/mobile/MobileLayoutContent';
import { useSession } from './session/SessionContext';
import SplashScreen from './SplashScreen';
import ConfirmationDialogProvider from '../../components/ConfirmationDialogProvider';
import useFormat from '../../hooks/useFormat';
import { NavigationItemType } from '../../types/module';
import { PermissionEnum } from '../../types/permission';
import { FETCH_THEME, FetchThemeType } from '../api/theme';
import useCustomRouter from '../hooks/useCustomRouter';

const AppLayout = ({ children }: { children: ReactNode }) => {
	const { isMobileFormat } = useFormat();
	const { checkPermission } = useSession();
	const { query, push } = useCustomRouter();

	const { data: themeData } = useQuery<FetchThemeType>(FETCH_THEME);

	const themeColors = useMemo(() => themeData?.theme, [themeData]);

	const navigateToRoute = useCallback(
		async ({ route, subItems }: Pick<NavigationItemType, 'route' | 'subItems'>) => {
			// Redirect the user to brochure/general/ongoing if the user doesn't have permission to validate or sign a brochure
			if (
				route === '/brochure/general' &&
				!checkPermission(PermissionEnum.BROCHURE_VALIDATE) &&
				!checkPermission(PermissionEnum.BROCHURE_SIGN) &&
				subItems?.some(({ route: concernedRoute }) => concernedRoute === '/ongoing')
			) {
				const newPathnameArray = [...route.split('/'), `/ongoing`?.replace(/^\//, '')].filter(Boolean);

				await push(`/${newPathnameArray.join('/')}`);

				return;
			}

			const { route: firstAllowedSubItemRoute, subItems: subSubItems } =
				subItems?.find(({ permissionCodes }) => permissionCodes?.some(checkPermission) ?? true) ?? {};

			const newPathnameArray = [...route.split('/'), firstAllowedSubItemRoute?.replace(/^\//, '')].filter(Boolean);

			if (subSubItems && firstAllowedSubItemRoute) {
				await navigateToRoute({ route: `/${newPathnameArray.join('/')}`, subItems: subSubItems });

				return;
			}

			const { dossier, ...restQuery } = query || {};

			await push({
				pathname: `/${newPathnameArray.join('/')}`,
				query: newPathnameArray.includes('brochure') ? restQuery : query,
			});
		},
		[checkPermission, push, query],
	);

	return themeColors ? (
		<HarfangThemeProvider {...themeColors}>
			{(harfangTheme) => (
				<ThemeProvider theme={harfangTheme}>
					<ConfirmationDialogProvider>
						<Layout>
							{!isMobileFormat ? (
								<DesktopLayoutContent onNavigation={navigateToRoute}>{children}</DesktopLayoutContent>
							) : (
								<MobileLayoutContent>{children}</MobileLayoutContent>
							)}
						</Layout>
					</ConfirmationDialogProvider>
				</ThemeProvider>
			)}
		</HarfangThemeProvider>
	) : (
		<SplashScreen description="Chargement du thème..." />
	);
};

export default AppLayout;
